import { HttpClient } from "@angular/common/http";
import { Component, Injector, OnDestroy } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Observable, Subject } from "rxjs";
import { debounceTime } from "rxjs/internal/operators/debounceTime";
import { take, takeUntil } from "rxjs/operators";
import { PublicService } from "src/app/services/public.service";
import { UpdateQuestionOutput } from "../../components/entities/custom-questions.component";
import { SocialLinksData, SocialLinksDialogComponent } from "../../components/social-links/socialLinks.dialog.component";
import { EntityBase } from "globals/store/entity.shared.data";
import { SubmitEntityData } from "globals/store/share.data";
import { getXYFromEventAndWidth, sleepForMS } from './../../helpers/helpers';
import { getJoiTemplate } from "globals/store/publish.data";
import publicHelpers from "globals/helpers/public-helpers";

export class ShareServices {
    static router: Router
    static dialog: MatDialog
    static publicService: PublicService
    static http: HttpClient
    static initialize(injector: Injector) {
      ShareServices.http = injector.get(HttpClient)
      ShareServices.router = injector.get(Router)
      ShareServices.dialog = injector.get(MatDialog)
      ShareServices.publicService = injector.get(PublicService)
    }
}

@Component({
    template: '',
    standalone: true,
    imports: []
})
export class ShareWrapper<Child> implements OnDestroy {
  $destroy: Subject<boolean> = new Subject()
  helpers = publicHelpers
  error: string
  ERROR_DISPLAY_MS = 5000
  loading: boolean
  processing: boolean
  submitted = false
  template = getJoiTemplate()
  imageHints = ['JPG, PNG, GIF or SVG', '10MB maximum']
  public get dialog():  MatDialog { return ShareServices.dialog }
  public get router(): Router { return ShareServices.router }
  public get publicService(): PublicService { return ShareServices.publicService }
  public get http(): HttpClient { return ShareServices.http }

  openDialogGetResult = <T extends unknown, C = any>(component: any, config?: MatDialogConfig): Promise<T> => this.dialog.open(component, config).afterClosed().toPromise()
  ngOnDestroy() { this.$destroy.next(true); this.$destroy.complete() }

  subObservableWithDebounce = <T>(obs: Observable<T>, debounceMS: number, callback: (val: T) => void, destroySubject: Subject<boolean> = null) => 
    obs.pipe(debounceTime(debounceMS)).pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => callback(val))

  take1 = <T>(obs: Observable<T>): Promise<T> =>
    obs.pipe(take(1)).toPromise()

  subCallback = <T> (obs: Observable<T>, callback: (val: T) => void, destroySubject: Subject<boolean> = null) => 
    obs.pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => callback(val))

  subProp = <T extends Child[K], K extends keyof Child> (obs: Observable<T>, prop: K, destroySubject: Subject<boolean> = null) => 
    obs.pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => (this as unknown as Child)[prop] = val)

  async showError(errorMessage: string) {
    this.error = errorMessage
    await sleepForMS(this.ERROR_DISPLAY_MS)
    this.error = null
  } 

  async submitEntity(submitData: SubmitEntityData, errorMessage: string) {
    
    this.processing = true
    const successfullySubmitted = await this.publicService.submitEntity(submitData)
    this.processing = false

    if (successfullySubmitted) this.submitted = true
    else this.showError(errorMessage)
  }

  async openSocialLinks(event, entity: EntityBase) {
    const data: SocialLinksData = { social: entity.social, ...getXYFromEventAndWidth(event, 300), template: this.template }
    await this.openDialogGetResult(SocialLinksDialogComponent, { width: '300px', data, disableClose: true, panelClass: 'vertical-center' })
    entity.social = data.social
  }

  updateCustomQuestion(data: UpdateQuestionOutput, entity: EntityBase) {
    const { id, answer } = data
    entity.customQuestions[id] = answer
  }

}
